import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";

export interface SearchOptions {
  searchResultClass: string;
  searchResultCurrentClass: string;
}

export interface SearchStorage {
  searchTerm: string;
  searchCaseSensitive: boolean;
  currentMatchIndex: number;
}

const searchPluginKey = new PluginKey("search");

declare module "@tiptap/core" {
  interface Commands<ReturnType> {
    search: {
      setSearchTerm: (searchTerm: string) => ReturnType;
      setSearchCaseSensitive: (sensitive: boolean) => ReturnType;
      setSearchCurrentMatch: (index: number) => ReturnType;
      clearSearch: () => ReturnType;
    };
  }
}

export const SearchExtension = Extension.create<SearchOptions, SearchStorage>({
  name: "search",

  addOptions() {
    return {
      searchResultClass: "chapter-search-highlight",
      searchResultCurrentClass: "chapter-search-highlight-current",
    };
  },

  addStorage() {
    return {
      searchTerm: "",
      searchCaseSensitive: false,
      currentMatchIndex: 0,
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: searchPluginKey,
        state: {
          init() {
            return DecorationSet.empty;
          },
          apply: (tr, oldState) => {
            const { searchTerm, searchCaseSensitive, currentMatchIndex } = this.storage;
            const { searchResultClass, searchResultCurrentClass } = this.options;

            if (!tr.docChanged && !tr.getMeta("searchUpdate")) {
              return oldState;
            }

            if (!searchTerm) {
              return DecorationSet.empty;
            }

            const doc = tr.doc;
            const decorations: Decoration[] = [];
            let matchIndex = 0;
            const searchText = searchCaseSensitive ? searchTerm : searchTerm.toLowerCase();

            doc.descendants((node, pos) => {
              if (node.isText) {
                const text = searchCaseSensitive ? node.text || "" : node.text?.toLowerCase() || "";
                let index = 0;
                while ((index = text.indexOf(searchText, index)) !== -1) {
                  const from = pos + index;
                  const to = from + searchTerm.length;
                  const isCurrent = matchIndex === currentMatchIndex;

                  decorations.push(
                    Decoration.inline(from, to, {
                      class: isCurrent ? searchResultCurrentClass : searchResultClass,
                    })
                  );

                  matchIndex++;
                  index += searchText.length;
                }
              }
            });

            return DecorationSet.create(doc, decorations);
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },

  addCommands() {
    return {
      setSearchTerm:
        (searchTerm: string) =>
        ({ tr, dispatch }) => {
          this.storage.searchTerm = searchTerm;
          this.storage.currentMatchIndex = 0;
          if (dispatch) dispatch(tr.setMeta("searchUpdate", true));
          return true;
        },
      setSearchCaseSensitive:
        (sensitive: boolean) =>
        ({ tr, dispatch }) => {
          this.storage.searchCaseSensitive = sensitive;
          if (dispatch) dispatch(tr.setMeta("searchUpdate", true));
          return true;
        },
      setSearchCurrentMatch:
        (index: number) =>
        ({ tr, dispatch }) => {
          this.storage.currentMatchIndex = index;
          if (dispatch) dispatch(tr.setMeta("searchUpdate", true));
          return true;
        },
      clearSearch:
        () =>
        ({ tr, dispatch }) => {
          this.storage.searchTerm = "";
          this.storage.currentMatchIndex = 0;
          if (dispatch) dispatch(tr.setMeta("searchUpdate", true));
          return true;
        },
    };
  },
});
